import React from 'react';
import { Form, Input, Progress } from 'antd';
import { CheckCircleFilled, CloseCircleOutlined } from '@ant-design/icons';

import { checkRules, firstPasswordIssue, passwordStrength } from '@lfoc/domain/auth/password-policy.js';
import { TONE } from '../config/palette.js';

// From weak to strong, in the palette's status tones (config/palette.js).
const STRENGTH_COLOUR = [TONE.danger.text, TONE.danger.text, TONE.warning.text, TONE.success.text, TONE.success.text];

/**
 * A new password and its confirmation, with the policy's checklist and a
 * strength meter as the person types (IP-79). The rules are
 * @lfoc/domain/auth/password-policy.js's - the same module the Console API
 * checks with before it asks Cognito - so the form never accepts what the
 * server would refuse.
 *
 * @param {{ name?: string, label?: string, person?: { email?, firstName?, lastName? }, currentField?: string }} props
 *   currentField - on the change-password form, the field holding the password
 *   being replaced, which the new one must differ from
 */
const PasswordField = ({ name = 'newPassword', label = 'New password', person = {}, currentField = null }) => {
    const form = Form.useFormInstance();
    const value = Form.useWatch(name, form) ?? '';
    const current = Form.useWatch(currentField ?? '__none__', form);
    const rules = checkRules(value, person);
    const strength = passwordStrength(value, person);

    return (
        <>
            <Form.Item
                name={name}
                label={label}
                dependencies={currentField ? [currentField] : []}
                rules={[{
                    validator: (_, v) => {
                        const issue = firstPasswordIssue(v ?? '', { ...person, current: currentField ? current : undefined });
                        return issue ? Promise.reject(new Error(issue)) : Promise.resolve();
                    }
                }]}
            >
                <Input.Password size="large" autoComplete="new-password" />
            </Form.Item>
            {value !== '' && (
                <div className="password-meter" aria-live="polite">
                    <Progress
                        percent={(strength.score + 1) * 20}
                        showInfo={false}
                        size="small"
                        strokeColor={STRENGTH_COLOUR[strength.score]}
                    />
                    <span className="password-meter-label">Strength: {strength.label}</span>
                </div>
            )}
            <ul className="password-rules">
                {rules.map((rule) => (
                    <li key={rule.code} className={rule.met ? 'is-met' : ''}>
                        {rule.met ? <CheckCircleFilled /> : <CloseCircleOutlined />} {rule.text}
                    </li>
                ))}
            </ul>
            <Form.Item
                name={name + 'Confirm'}
                label="Confirm the new password"
                dependencies={[name]}
                rules={[
                    { required: true, message: 'Enter the new password again.' },
                    ({ getFieldValue }) => ({
                        validator: (_, v) => (!v || getFieldValue(name) === v
                            ? Promise.resolve()
                            : Promise.reject(new Error('The two passwords do not match.')))
                    })
                ]}
            >
                <Input.Password size="large" autoComplete="new-password" />
            </Form.Item>
        </>
    );
};

export default PasswordField;
